import { BehaviorSubject } from 'rxjs';
import { addError } from './Error';
import { useSnackbar } from '../Context/SnackbarContext';
import { createLineItemApi, deleteLineItemApi, getLineItemsApi, updateLineItemApi } from '../Api/LineItems/lineItemApi';
import { getProductById } from './Product';

export type LineItem = {
  id: string;
  outside_inventory_id: string;
  product_id: string;
  description: string;
  mrc: number;
  nrc: number;
};

const lineItemsSubject = new BehaviorSubject<LineItem[]>([]);
const lineItemsLoadingSubject = new BehaviorSubject<boolean>(false);
// The subject is shared across the drawer and the full-list page, so a slower earlier request could
// otherwise resolve last and overwrite the record the user is actually looking at.
let latestRequestedId: string | null = null;

export const getLineItemSubject = (): BehaviorSubject<LineItem[]> => lineItemsSubject;
export const getLineItemLoadingSubject = (): BehaviorSubject<boolean> => lineItemsLoadingSubject;
export const getLineItemTotal = () =>
  Number(
    getLineItemSubject()
      .getValue()
      .reduce((sum, item) => sum + (Number(item.mrc) || 0), 0)
      .toFixed(2)
  );

export const getLineItemProductLabel = (product_id: string): string => {
  const product = getProductById(product_id);
  if (!product) return '';
  return `${product.family}-${product.name}`;
};

export interface UseLineItemsStoreReturn {
  fetchLineItems: (outside_inventory_id: string) => Promise<void>;
  createLineItem: (lineItem: LineItem) => Promise<void>;
  updateLineItem: (lineItem: LineItem) => Promise<void>;
  deleteLineItem: (id: string) => Promise<void>;
  setLineItems: (lineItem: LineItem[]) => void;
}

export const useLineItemsStore = (): UseLineItemsStoreReturn => {
  const { setSnack } = useSnackbar();

  const fetchLineItems = async (outside_inventory_id: string): Promise<void> => {
    latestRequestedId = outside_inventory_id;
    lineItemsLoadingSubject.next(true);

    const { data, error } = await getLineItemsApi(outside_inventory_id);

    if (latestRequestedId !== outside_inventory_id) return;

    if (data) lineItemsSubject.next(data);
    if (error) addError('line-item', error.message);

    lineItemsLoadingSubject.next(false);
  };

  const createLineItem = async (lineItem: LineItem) => {
    const { data, error } = await createLineItemApi(lineItem);
    if (data) {
      if (latestRequestedId === lineItem.outside_inventory_id) {
        setLineItems([...getLineItemSubject().getValue(), data]);
      }
      setSnack({ message: 'Line item saved successfully', type: 'success', open: true });
    }

    if (error) {
      setSnack({ message: 'Error saving line item', type: 'error', open: true });
    }
  };

  const updateLineItem = async (lineItem: LineItem) => {
    const { data, error } = await updateLineItemApi(lineItem);
    if (data) {
      const currentState = [...getLineItemSubject().getValue()];
      const updatedLineItems = currentState.map((l: LineItem) => (l.id === data.id ? data : l));
      setLineItems(updatedLineItems);
      setSnack({ message: 'Line item updated successfully', type: 'success', open: true });
    }
    if (error) {
      setSnack({ message: 'Error updating comment', type: 'error', open: true });
    }
  };

  const deleteLineItem = async (id: string) => {
    const { error } = await deleteLineItemApi(id);
    if (error) return setSnack({ message: 'Error deleting line item', type: 'error', open: true });

    const currentState = [...getLineItemSubject().getValue()];
    const lineItems = currentState.filter((l: LineItem) => l.id !== id);
    setLineItems(lineItems);
    setSnack({ message: 'Line item deleted successfully', type: 'success', open: true });
  };

  const setLineItems = (lineItems: LineItem[]) => lineItemsSubject.next(lineItems);

  return {
    fetchLineItems,
    createLineItem,
    updateLineItem,
    deleteLineItem,
    setLineItems
  };
};
